const PASSES = {
  overhead: ['↑', 'Fly over the guardian.'],
  flank: ['↗', 'Fly alongside the guardian.'],
  underwing: ['↓', 'Fly below the wing.'],
};

function leviathanCue(live) {
  const descriptions = {
    arrival: ['prepare', '◇', 'AIM', 'Watch for the highlighted target.'],
    perimeter: ['objective', '◎', 'SHOOT', 'Shoot the highlighted escort lenses.'],
    entry: ['objective', '⇥', '→', 'Fly through the highlighted mouth opening.'],
    throat: ['dodge', '↔', 'MOVE', 'Move through the gaps between the machinery.'],
    chamber: ['objective', '◎', 'SHOOT', 'Shoot the highlighted pressure core.'],
    breakout: ['prepare', '◇', '→', 'Follow the opening through the hull.'],
    wound: ['objective', '◎', 'SHOOT', 'Shoot highlighted parts through the opening.'],
    crash: ['prepare', '◇', '↘', 'Watch the guardian fall.'],
    escaping: ['prepare', '↗', '→', 'The guardian is leaving.'],
  };
  if (
    ['perimeter', 'chamber', 'wound'].includes(live.encounter?.phase) &&
    !live.components?.some((part) => part.vulnerable && part.hp > 0)
  )
    return { mode: 'prepare', label: '◇', title: 'AIM', copy: 'Watch for the open target ring.' };
  if (live.encounter?.phase === 'entry' && live.systems?.jaw !== 1)
    return {
      mode: 'prepare',
      label: '◇',
      title: '→',
      copy: 'The mouth is opening. Follow the corner outline when it appears.',
    };
  const entry = descriptions[live.encounter?.phase];
  if (!entry) return null;
  const [mode, label, title, copy] = entry;
  return { mode, label, title, copy };
}

/** Minimal visible prompts follow canonical combat state. Longer descriptions
 * remain available to assistive technology; target/portal outlines carry the
 * spatial instruction. A caption never opens a damage window. */
export function skyBossControlCue(state, active) {
  const boss = state.boss;
  if (!active || state.status !== 'playing' || state.traversal || !boss || boss.defeated)
    return null;
  if (state.leviathan) return leviathanCue(state.leviathan);
  if (state.cathedral) {
    const encounter = state.cathedral;
    const locked = Object.values(encounter.legs ?? {}).some(
      (leg) => !leg.broken && leg.lockedUntil > encounter.age,
    );
    const copy = {
      arrival: 'Find the grounding rods beneath the six knees.',
      grounding: locked
        ? 'SHOOT THE LOCKED KNEE · its grounding mechanism is exposed.'
        : `ROD READY · ${(state.cathedralGuidance?.legId ?? 'nearest').replaceAll('-', ' ').toUpperCase()} · hold below the copper rod, then dodge the committed strike.`,
      arrest: 'The front supports are failing. Prepare to fly underneath.',
      underpass: 'FLY UNDERNEATH · pass through both openings and clear the falling hull.',
      kneel: 'The cathedral is tipping. Stay clear while the nave opens.',
      nave: encounter.enteredNave
        ? 'SHOOT THE GENERATOR · the nave is open.'
        : 'ENTER THE NAVE · fly through the central aisle opening.',
      discharge: 'Generator destroyed. Clear the falling cathedral.',
    }[encounter.phase];
    return copy ? { mode: 'objective', label: '◎', title: 'OBJECTIVE', copy } : null;
  }

  if (
    (boss.cinderheart ||
      boss.prism ||
      boss.mourning ||
      boss.orchid ||
      boss.hourglass ||
      boss.vesper ||
      boss.scrap ||
      boss.twins) &&
    typeof boss.controlHint === 'string'
  )
    return { mode: 'objective', label: '◎', title: 'OBJECTIVE', copy: boss.controlHint };
  if (boss.controlMode === 'aim-approach')
    return { mode: 'prepare', label: '◇', title: 'AIM', copy: 'Aim at the highlighted crest.' };
  if (boss.controlMode === 'aim') {
    const ready = boss.aimWindow === true;
    return {
      mode: ready ? 'aim' : 'prepare',
      label: ready ? '◎' : '◇',
      title: ready ? 'SHOOT' : 'AIM',
      copy: ready
        ? 'The crest is open. Aim inside the brackets.'
        : 'Aim at the crest. Wait for the brackets to open.',
    };
  }
  if (boss.controlMode === 'steer')
    return {
      mode: 'dodge',
      label: '↔',
      title: 'MOVE',
      copy:
        boss.attackPhase === 'charge'
          ? 'Fire is coming. Move clear of its path.'
          : 'Move clear of the flames.',
    };
  return null;
}

/** Accessible route status owns no combat clock. The controller passes its
 * abort signal and consumes skip at the next simulation step; returning is a
 * visible protected movement, never an immediate unannounced teleport. */
export function createSkyTraversalHud({ e, surface, signal, onSkip }) {
  const root = e('div', 'sky-traversal');
  const label = e('p', 'sky-traversal-label', '◇');
  const title = e('h4', 'sky-traversal-title');
  const copy = e('p', 'sky-traversal-copy');
  const skip = e('button', 'sky-button sky-traversal-skip', '→');
  root.setAttribute('aria-live', 'polite');
  skip.setAttribute('aria-label', 'Return to combat');
  root.append(label, title, copy, skip);
  surface.append(root);
  skip.addEventListener('click', onSkip, { signal });
  let previous = null,
    lastCaption = null;
  return {
    paint(state, active, settings = {}) {
      const route = state.traversal;
      const cue = skyBossControlCue(state, active, settings);
      root.hidden = !active || (!route && !cue);
      root.dataset.control = route ? 'flyby' : (cue?.mode ?? 'none');
      skip.hidden = !route;
      const identity = route?.id ?? (route ? route.phase : null);
      const changed = identity !== previous;
      previous = identity;
      if (!route) {
        const key = cue && `${cue.mode}:${cue.title}:${cue.copy}`;
        if (key !== lastCaption && cue) {
          label.textContent = cue.label;
          title.textContent = cue.title;
          copy.textContent = cue.copy;
          root.setAttribute('aria-label', `${cue.title}. ${cue.copy}`);
        }
        lastCaption = key;
        return changed;
      }
      const returning = route.beat === 'return';
      const captionKey = `${identity}:${route.phase}:${returning}`;
      if (captionKey === lastCaption) return changed;
      lastCaption = captionKey;
      label.textContent = '◇';
      const caption = PASSES[route.phase] ?? PASSES[route.kind] ?? PASSES.overhead;
      title.textContent = returning ? '↩' : caption[0];
      copy.textContent = returning ? 'Returning to the target.' : caption[1];
      root.setAttribute('aria-label', copy.textContent);
      skip.disabled = returning;
      skip.textContent = returning ? '↩' : '→';
      return changed;
    },
  };
}
